/**
 * RecentProcessActivity widget — latest activity across all process
 * instances in the tenant.
 *
 * Migrated to live API in Wave 3.6. Reads GET /v1/processes/instances
 * and renders the most-recently-updated 8 entries. The mock listed
 * per-event entries (started / completed / rejected / assigned /
 * escalated) but the backend surfaces instance-level status rather
 * than per-event log, so the widget maps the latest instance status
 * to the closest mock semantic:
 *
 *   instance.status → widget event type
 *   -----------------------------------
 *   completed       → 'completed'
 *   rejected        → 'rejected'
 *   failed          → 'escalated'
 *   waiting         → 'assigned'
 *   running / new   → 'started'
 *
 * When the backend's process audit log / timeline endpoint lands (ADR-015+
 * engine work), this widget can drop the status mapping and consume the
 * real per-event stream.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, CircularProgress, List, ListItem, Paper, Typography,
} from '@mui/material';
import {
  AssignmentOutlined, CancelOutlined, CheckCircleOutlined,
  PauseCircleOutlined, PlayCircleOutlined,
} from '@mui/icons-material';
import { ApiError, processesApi } from '../../../../api';
import { WidgetErrorState } from '../../../../components/primitives';
import { queryDashboard } from '../../../../api/dashboardQuery';
import type { ProcessInstance } from '../../../../api/processes';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from './widgetDisplay';
import WidgetSummaryLine from './WidgetSummaryLine';
import { translateApiError } from '../../../../i18n/translateApiError';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';
import { TimelineOutlined } from '@mui/icons-material';

type EventType = 'started' | 'completed' | 'rejected' | 'assigned' | 'escalated';

const typeConfig: Record<EventType, { icon: React.ReactNode; color: string }> = {
  started: { icon: <PlayCircleOutlined sx={{ fontSize: '0.9rem' }} />, color: '#2196F3' },
  completed: { icon: <CheckCircleOutlined sx={{ fontSize: '0.9rem' }} />, color: '#4CAF50' },
  rejected: { icon: <CancelOutlined sx={{ fontSize: '0.9rem' }} />, color: '#F44336' },
  assigned: { icon: <AssignmentOutlined sx={{ fontSize: '0.9rem' }} />, color: '#FF9800' },
  escalated: { icon: <PauseCircleOutlined sx={{ fontSize: '0.9rem' }} />, color: '#9C27B0' },
};

function mapStatus(s: string | null | undefined): EventType {
  if (s === 'completed' || s === 'approved') return 'completed';
  if (s === 'rejected' || s === 'cancelled' || s === 'canceled') return 'rejected';
  if (s === 'failed' || s === 'exception' || s === 'error') return 'escalated';
  if (s === 'waiting' || s === 'pending') return 'assigned';
  return 'started';
}

function avatarColor(seed: string): string {
  const palette = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#00BCD4'];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
}

function initial(seed: string | null | undefined): string {
  if (!seed) return '•';
  return seed.slice(0, 1).toUpperCase();
}

const RecentProcessActivity: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [instances, setInstances] = useState<ProcessInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await queryDashboard(
          'process-instances',
          () => processesApi.processInstancesApi.list(),
        );
        if (cancelled) return;
        // Sort by whichever timestamp is most recent (completed_at
        // overrides started_at overrides created_at).
        const stamp = (r: ProcessInstance) =>
          Date.parse(r.completed_at ?? r.started_at ?? r.created_at ?? '') || 0;
        const sorted = [...rows].sort((a, b) => stamp(b) - stamp(a));
        setInstances(sorted.slice(0, 8));
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.recentProcessActivity.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <WidgetErrorState error={error} />;
  }

  if (instances.length === 0) {
    return (
      <WidgetEmptyState icon={TimelineOutlined} title={t('dashboard:widgets.recentProcessActivity.noActivity')} />
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <WidgetSummaryLine>
        {t('dashboard:widgets.recentProcessActivity.latestActivity')}
      </WidgetSummaryLine>
      <List sx={{ py: 0, flex: 1, overflow: 'auto' }}>
        {instances.map((inst, i) => {
          const eventType = mapStatus(inst.status);
          const cfg = typeConfig[eventType];
          const title = inst.instance_number ?? inst.id.slice(0, 12);
          const actor = inst.assigned_to ?? inst.requested_by ?? t('dashboard:widgets.recentProcessActivity.system');
          const stamp = inst.completed_at ?? inst.started_at ?? inst.created_at;
          return (
            <ListItem
              key={inst.id}
              component={Paper}
              elevation={0}
              onClick={() => navigate('/processes/instances/active')}
              sx={{ py: 1, mb: i < instances.length - 1 ? 0.5 : 0,
                borderRadius: 1, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 1,
                cursor: 'pointer',
                border: '1px solid transparent',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'divider' },
              }}
            >
              <Box sx={{ color: cfg.color, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                {cfg.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2"
                  sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                  {title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                  <Avatar sx={{ width: 14, height: 14,
                    bgcolor: avatarColor(actor), fontSize: '0.5rem', fontWeight: 700 }}>
                    {initial(actor)}
                  </Avatar>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {actor.length > 16 ? `${actor.slice(0, 8)}…` : actor}
                  </Typography>
                  <Typography variant="caption"
                    sx={{ fontSize: '0.75rem', color: cfg.color, fontWeight: 600 }}>
                    · {t(`dashboard:widgets.recentProcessActivity.${eventType}`)}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary"
                sx={{ fontSize: '0.75rem', flexShrink: 0 }}>
                {formatRelativeTime(stamp, t, i18n.language)}
              </Typography>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default RecentProcessActivity;
